import React, { FC, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { LangValue } from '../../../states/types/common'
import { URL } from '../../constants/url'
import { setUrlId, setPath } from '../../../utils/url'
import LineChart from './LineChart'
import { addMinutes, dateManipulateDay, diffDays, format, nowDate, secToHour, secToMinute, secToSec } from '../../../utils/date'
import Tips from '../common/Tips'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import LoadingSquare from './LoadingSquare'

interface Schedule {
  start_time: string
  end_time: string
  title: string
  place: string | null
  participant: string | null
}

interface Member {
  id: number
  name: string
  kana: string | null
  code: string | null
  profile_image: string | null
  teams: {
    id: number
    name: string
  }[]
  work_time: number
  inactive_time: number
  prev_date: string
  actions: {
    target_time: string
    today_value: number
    prev_value: number
  }[]
  applications: {
    target_time: string
    services: {
      name: string
      color: string | null
      icon: string | null
      time: number
      biz: number
      details: {
        name: string
        time: number
        biz: number
      }[]
    }[]
  }[]
  schedules: Schedule[]
}

interface TimelineProps {
  member: Member | undefined
  targetDate: string
  displayHeader?: boolean
  maxValue?: number
}

const Timeline: FC<TimelineProps> = (props) => {
  const { language } = useParams<{ language: LangValue }>()
  const {
    member,
    targetDate,
    displayHeader,
    maxValue,
  } = props

  const [openAppNumber, setOpenAppNumber] = useState<number>()
  const [openScheduleNumber, setOpenScheduleNumber] = useState<number>()

  const [openDetail, setOpenDetail] = useState<boolean>(true)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const appModalDetail = useMemo(() => {
    if (openAppNumber === undefined || !member) return { startTime: "", endTime: "", services: [] }
    const target = member.applications[openAppNumber]
    const startTime = format(target.target_time, "H:mm")
    const endTime = addMinutes(target.target_time, 30, "H:mm")
    return {
      startTime: startTime,
      endTime: endTime,
      services: target.services,
    }
  }, [openAppNumber])

  const scheduleModalDetail = useMemo(() => {
    if (openScheduleNumber === undefined || !member) return { startTime: "", endTime: "", title: "", place: "", participant: "" }
    const target = member.schedules[openScheduleNumber]
    return {
      startTime: target.start_time,
      endTime: target.end_time,
      title: target.title,
      place: target.place,
      participant: target.participant,
    }
  }, [openScheduleNumber])

  const buildTime = (sec: number) => {
    const minutes = Number(secToMinute(sec)) ? `${secToMinute(sec)}分` : ""
    const seconds = Number(secToSec(sec)) ? `${secToSec(sec)}秒` : ""
    return `${minutes}${seconds}`
  }

  const buildIcon = (icon: string | null) => {
    if (!icon) {
      return (
        <FontAwesomeIcon icon={["fas", "question"]} className="icon" />
      )
    }
    const iconWords = icon.split(" ")
    return (
      <FontAwesomeIcon icon={[iconWords[0] as IconPrefix, (iconWords[1].slice(3) || "question") as IconName]} className="icon" />
    )
  }

  const buildSchedulePosition = (data: Schedule, type: string) => {
    if (!data) return 0
    let position = 0
    if (type === "start") {
      if (Number(format(data.start_time, "YYYYMMDD")) === Number(format(targetDate, "YYYYMMDD"))) {
        const tmp = (Number(format(data.start_time, "H")) * 60) + Number(format(data.start_time, "m"))
        position = tmp < 300 ? 0 : tmp - 300
      } else if (Number(format(data.start_time, "YYYYMMDD")) > Number(format(targetDate, "YYYYMMDD"))) {
        const tmp = (Number(format(data.start_time, "H")) * 60) + Number(format(data.start_time, "m"))
        position = tmp < 300 ? tmp + 1140 : 1440
      }
      return position
    }
    if (type === "end") {
      if (Number(format(data.end_time, "YYYYMMDD")) === Number(format(targetDate, "YYYYMMDD"))) {
        const tmp = (Number(format(data.end_time, "H")) * 60) + Number(format(data.end_time, "m"))
        position = tmp < 300 ? 0 : tmp - 300
      } else if (Number(format(data.end_time, "YYYYMMDD")) > Number(format(targetDate, "YYYYMMDD"))) {
        if (diffDays(targetDate, data.end_time) > 1) {
          position = 1440
        } else {
          const tmp = (Number(format(data.end_time, "H")) * 60) + Number(format(data.end_time, "m"))
          position = tmp < 300 ? tmp + 1140 : 1440
        }
      }
      return position
    }
    return position
  }

  const buildScheduleBar = (data: Schedule, index: number) => {
    if (!data) return (<></>)
    const startPosition = buildSchedulePosition(data, "start")
    const endPosition = buildSchedulePosition(data, "end")

    return (
      <div
        style={{
          position: "absolute",
          top: "2px",
          left: `calc((100% / 1440) * ${startPosition})`,
          width: `calc((100% / 1440) * ${endPosition - startPosition})`,
          height: "30px",
          color: "#FFFFFF",
          backgroundColor: (openScheduleNumber === undefined || openScheduleNumber === index) ? "rgba(0, 204, 204, 0.6)" : "rgba(153, 153, 153, 0.6)",
          borderRadius: "6px",
          fontSize: "10px",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          padding: "0 4px",
          overflow: "hidden",
          cursor: "pointer",
        }}
        onClick={() => setOpenScheduleNumber(index)}
        role="none"
      >
        {data.title}
      </div>
    )
  }

  useEffect(() => {
    if (openScheduleNumber === undefined) return
    if (openAppNumber !== undefined) {
      setOpenAppNumber(undefined)
    }
  }, [openScheduleNumber])

  useEffect(() => {
    if (openAppNumber === undefined) return
    if (openScheduleNumber !== undefined) {
      setOpenScheduleNumber(undefined)
    }
  }, [openAppNumber])

  useEffect(() => {
    if (!member) return
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }, [member])

  return (
    <div className="box">
      {
        displayHeader && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: openDetail ? "1px solid #cccccc" : "none",
              paddingBottom: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {
                member?.teams.map((team) => (
                  <div
                    key={`${member.name}_${team.name}`}
                    style={{
                      whiteSpace: "nowrap",
                      border: "1px solid #999999",
                      padding: "6px 12px",
                      fontSize: "14px",
                      borderRadius: "4px",
                    }}
                  >
                    {team.name}
                  </div>
                ))
              }
              {
                member?.profile_image ? (
                  <img
                    alt="profile"
                    src={member.profile_image}
                    className="profile_icon"
                    style={{ width: "24px", height: "24px", borderRadius: "50%", marginRight: 0 }}
                  />
                ) : (
                  <img
                    alt=""
                    src={`/static/images/profile/${(Number(member?.id) || 0) % 12}.jpeg`}
                    className="profile_icon"
                    style={{ width: "24px", height: "24px", borderRadius: "50%", marginRight: 0 }}
                  />
                )
              }
              <Link to={setPath(setUrlId(URL.RISK_MEMBER_DETAIL.path, 'id', member?.id || 0), language)} style={{ fontSize: "16px" }}>
                {member?.name}
              </Link>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {
                !!member?.work_time && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "2px",
                    }}
                  >
                    開始〜終了合計時間:
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "2px",
                      }}
                    >
                      {
                        !!secToHour(member.work_time || 0) && (
                          <>
                            <strong style={{ fontSize: "22px", color: "#4980E5" }}>
                              {secToHour(member.work_time || 0)}
                            </strong>
                            時間
                          </>
                        )
                      }
                      {
                        !!secToMinute(member.work_time || 0) && (
                          <>
                            <strong style={{ fontSize: "22px", color: "#4980E5" }}>
                              {secToMinute(member.work_time || 0)}
                            </strong>
                            分
                          </>
                        )
                      }
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "2px",
                      }}
                    >
                      (無操作時間:
                      {
                        !!secToHour(member.inactive_time || 0) && (
                          <>
                            <strong style={{ fontSize: "16px", color: "#EC6A5E" }}>
                              {secToHour(member.inactive_time || 0)}
                            </strong>
                            時間
                          </>
                        )
                      }
                      {
                        !!secToMinute(member.inactive_time || 0) && (
                          <>
                            <strong style={{ fontSize: "16px", color: "#EC6A5E" }}>
                              {secToMinute(member.inactive_time || 0)}
                            </strong>
                            分
                          </>
                        )
                      }
                      を含む)
                    </div>
                  </div>
                )
              }
              {
                member?.work_time ? (
                  <button type="button" onClick={() => setOpenDetail(!openDetail)}>
                    <FontAwesomeIcon icon={['fas', openDetail ? 'minus' : 'plus']} />
                  </button>
                ) : (
                  <></>
                )
              }
            </div>
          </div>
        )
      }
      {
        openDetail && (
          <>
            {
              isLoading ? (
                <LoadingSquare>
                  <>
                    グラフを描画しています…
                  </>
                </LoadingSquare>
              ) : (
                <LineChart
                  list={
                    member?.actions.map((action) => (
                      {
                        label: format(action.target_time, "H:mm"),
                        currentValue: action.today_value,
                        pastValue: action.prev_value,
                      }
                    )) || []
                  }
                  chartSize={120}
                  labelSkip={6}
                  targetDate={targetDate || nowDate()}
                  prevDate={member?.prev_date || dateManipulateDay(targetDate, -1, "YYYY-MM-DD") || nowDate()}
                  maxValue={maxValue}
                />
              )
            }
            <div
              style={{
                margin: "16px 0 8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    textAlign: "center",
                    position: "relative",
                  }}
                >
                  <Tips
                    description="30分間で使用したアプリケーション"
                    className=""
                    icon={false}
                  >
                    <FontAwesomeIcon icon={['fas', 'tasks']} className="icon" />
                  </Tips>
                </div>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    border: "solid 1px #DDDDDD",
                    height: "36px",
                  }}
                >
                  {
                    Array(47).fill(null).map((_w, i) => (
                      <div
                        key={`line_app_${member?.id}_${i + 0}`}
                        style={{
                          position: "absolute",
                          top: "0",
                          left: `calc((100% / 48) * ${i + 1})`,
                          width: "1px",
                          height: "36px",
                          backgroundColor: i % 2 ? "#DDDDDD" : "#EEEEEE",
                        }}
                      />
                    ))
                  }
                  {
                    member?.applications.map((w, i) => (
                      <React.Fragment key={`app_${member.id}_${i + 0}_${w.target_time}`}>
                        {
                          !w.services.length ? (
                            <></>
                          ) : (
                            <div
                              style={{
                                position: "absolute",
                                top: "2px",
                                left: `calc(((100% / 48) * ${i}) + 1px)`,
                                width: "calc((100% / 48) - 2px)",
                                height: "30px",
                                color: "#FFFFFF",
                                backgroundColor: (openAppNumber === undefined || openAppNumber === i)
                                  ? (w.services[0].color || "#666666")
                                  : "#CCCCCC",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                                cursor: "pointer",
                              }}
                              onClick={() => setOpenAppNumber(i)}
                              role="none"
                            >
                              {buildIcon(w.services[0].icon)}
                            </div>
                          )
                        }
                      </React.Fragment>
                    ))
                  }
                  {
                    openAppNumber !== undefined && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: "36px",
                            left: `calc((100% / 48) * ${openAppNumber})`,
                            backgroundColor: "#EEEEEE",
                            boxShadow: "0px 0px 2px #000000",
                            width: "450px",
                            zIndex: 1999,
                            transform: "translateX(-50%)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              alignItems: "center",
                              padding: "8px",
                              gap: "8px",
                              backgroundColor: "#666666",
                              color: "#FFFFFF",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "12px",
                                }}
                              >
                                {`${member?.name}さんが${appModalDetail.startTime} ~ ${appModalDetail.endTime} の間で使用開始したアプリケーション合計`}
                              </div>
                              <button
                                type="button"
                                style={{
                                  padding: 0,
                                }}
                                onClick={() => setOpenAppNumber(undefined)}
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} className="icon" />
                              </button>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                width: "100%",
                                fontSize: "14px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: "4px",
                                }}
                              >
                                <div>
                                  使用時間合計
                                </div>
                                <div
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {buildTime(appModalDetail.services.reduce((s, v) => s + v.time, 0))}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: "4px",
                                }}
                              >
                                <div>
                                  操作量合計
                                </div>
                                <div
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {`${(appModalDetail.services.reduce((s, v) => s + v.biz, 0)).toLocaleString()}Biz`}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "8px",
                              padding: "16px",
                            }}
                          >
                            <div>
                              {`${appModalDetail.startTime} ~ ${appModalDetail.endTime} のアプリケーション別作業一覧`}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "8px",
                                maxHeight: "250px",
                                overflowY: "scroll",
                              }}
                            >
                              {
                                appModalDetail.services.map((v) => (
                                  <div
                                    key={`services_${member?.id}_${v.name}_${v.time}`}
                                    style={{
                                      border: `solid 1px ${v.color || "#666666"}`,
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "4px",
                                        backgroundColor: v.color || "#666666",
                                        color: "#FFFFFF",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "4px",
                                        }}
                                      >
                                        {buildIcon(v.icon)}
                                        <div
                                          style={{
                                            maxWidth: "240px",
                                            whiteSpace: "break-spaces",
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                          }}
                                        >
                                          {v.name}
                                        </div>
                                      </div>
                                      <div>
                                        {`${buildTime(v.time)} / ${v.biz}biz`}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexFlow: "column",
                                        gap: "4px",
                                        padding: "4px",
                                      }}
                                    >
                                      {
                                        v.details.map((w) => (
                                          <div
                                            key={`service_detail_${member?.id}_${v.name}_${w.name}_${w.time}`}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              padding: "4px",
                                              color: "#666666",
                                            }}
                                          >
                                            <div
                                              style={{
                                                maxWidth: "280px",
                                                wordBreak: "break-all",
                                              }}
                                            >
                                              {w.name || "タイトルなし"}
                                            </div>
                                            <div>
                                              {`${buildTime(w.time)} / ${w.biz}biz`}
                                            </div>
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    textAlign: "center",
                    position: "relative",
                  }}
                >
                  <Tips
                    description="連携されたスケジュール"
                    className=""
                    icon={false}
                  >
                    <FontAwesomeIcon icon={['fas', 'calendar-check']} className="icon" />
                  </Tips>
                </div>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    border: "solid 1px #DDDDDD",
                    height: "36px",
                  }}
                >
                  {
                    Array(47).fill(null).map((_w, i) => (
                      <div
                        key={`line_schedule_${member?.id}_${i + 0}`}
                        style={{
                          position: "absolute",
                          top: "0",
                          left: `calc((100% / 48) * ${i + 1})`,
                          width: "1px",
                          height: "36px",
                          backgroundColor: i % 2 ? "#DDDDDD" : "#EEEEEE",
                        }}
                      />
                    ))
                  }
                  {
                    member?.schedules.map((w, i) => (
                      <React.Fragment key={`schedule_${member.id}_${i + 0}_${w.start_time}_${openScheduleNumber || 0}`}>
                        {buildScheduleBar(w, i)}
                      </React.Fragment>
                    ))
                  }
                  {
                    openScheduleNumber !== undefined && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: "36px",
                            left: `calc((100% / 1440) * ${buildSchedulePosition(
                              { ...scheduleModalDetail, start_time: scheduleModalDetail.startTime, end_time: scheduleModalDetail.endTime },
                              "start",
                            )})`,
                            backgroundColor: "#EEEEEE",
                            boxShadow: "0px 0px 2px #000000",
                            width: "450px",
                            zIndex: 1999,
                            transform: "translateX(-50%)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              alignItems: "center",
                              padding: "8px",
                              gap: "4px",
                              backgroundColor: "#00cccc",
                              color: "#FFFFFF",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "14px",
                                }}
                              >
                                {scheduleModalDetail.title || "タイトルなし"}
                              </div>
                              <button
                                type="button"
                                style={{
                                  padding: 0,
                                }}
                                onClick={() => setOpenScheduleNumber(undefined)}
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} className="icon" />
                              </button>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "16px",
                              padding: "16px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "14px",
                              }}
                            >
                              <FontAwesomeIcon icon={['far', 'clock']} className="icon" />
                              {`${format(scheduleModalDetail.startTime, "M/D H:mm")} ~ ${format(scheduleModalDetail.endTime, "M/D H:mm")}`}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                              }}
                            >
                              <div
                                style={{
                                  whiteSpace: "nowrap",
                                }}
                              >
                                場所：
                              </div>
                              <div
                                style={{
                                  wordBreak: "break-all",
                                }}
                              >
                                {scheduleModalDetail.place || "設定されていません"}
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                              }}
                            >
                              <div
                                style={{
                                  whiteSpace: "nowrap",
                                }}
                              >
                                参加者：
                              </div>
                              <div
                                style={{
                                  wordBreak: "break-all",
                                }}
                              >
                                {scheduleModalDetail.participant || "設定されていません"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
            </div>
          </>
        )
      }
    </div>
  )
}

Timeline.defaultProps = {
  displayHeader: true,
  maxValue: 1500,
}

export default Timeline
